import type { FC, ReactNode } from 'react'
import type { NavProps } from '@/types'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'

import { cn } from '@/lib/utils'
import { useTheme } from '@/hooks/use-theme'
import { MICRO } from '@/constants/type'
import { NAV_LINKS } from '@/constants/nav'
import { siteConfig, studio } from '@/constants/site'

export const Nav: FC<NavProps> = ({ variant = 'default' }): ReactNode => {
    const router = useRouter()
    const { theme, toggleTheme } = useTheme()

    const [scrolled, setScrolled] = useState(false)
    const [menuOpen, setMenuOpen] = useState(false)

    useEffect(() => {
        if (variant === 'immersive') return

        const onScroll = () => {
            setScrolled(window.scrollY > 24)
        }

        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })

        return () => {
            window.removeEventListener('scroll', onScroll)
        }
    }, [variant])

    useEffect(() => {
        setMenuOpen(false)
    }, [router.asPath])

    useEffect(() => {
        if (!menuOpen) return

        const previous = document.body.style.overflow
        document.body.style.overflow = 'hidden'

        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setMenuOpen(false)
        }

        window.addEventListener('keydown', onKey)

        return () => {
            document.body.style.overflow = previous
            window.removeEventListener('keydown', onKey)
        }
    }, [menuOpen])

    const isActive = (href: string) => {
        return href === '/' ? router.pathname === '/' : router.pathname.startsWith(href)
    }

    return (
        <>
            <header
                className={cn(
                    'fixed inset-x-0 top-0 z-[70] transition-[background-color,border-color] duration-500',
                    scrolled ? 'border-b' : 'border-b border-transparent'
                )}
                style={{
                    background: scrolled ? 'color-mix(in srgb, var(--fz-bg) 88%, transparent)' : 'transparent',
                    borderBottomColor: scrolled ? 'var(--fz-rule)' : 'transparent',
                    backdropFilter: scrolled ? 'blur(14px)' : undefined,
                    WebkitBackdropFilter: scrolled ? 'blur(14px)' : undefined
                }}
            >
                <div
                    className='pointer-events-none flex items-center justify-between px-6 py-5 sm:px-12 sm:py-7'
                    style={{ paddingTop: 'max(1.25rem, env(safe-area-inset-top))' }}
                >
                    <Link
                        href='/'
                        className='pointer-events-auto font-serif text-[19px] leading-none tracking-[-0.01em] transition-opacity duration-300 hover:opacity-60'
                        style={{ color: 'var(--fz-ink)' }}
                    >
                        {siteConfig.name}
                    </Link>

                    <nav aria-label='Primary' className='pointer-events-auto hidden items-center gap-9 sm:flex'>
                        {NAV_LINKS.map(link => (
                            <Link
                                key={link.href}
                                href={link.href}
                                aria-current={isActive(link.href) ? 'page' : undefined}
                                className={cn(MICRO, 'pb-1 transition-colors duration-300')}
                                style={{
                                    color: isActive(link.href) ? 'var(--fz-ink)' : 'var(--fz-faint)',
                                    borderBottom: '1px solid',
                                    borderBottomColor: isActive(link.href)
                                        ? 'var(--fz-rule-on)'
                                        : 'transparent'
                                }}
                            >
                                {link.label}
                            </Link>
                        ))}

                        <span aria-hidden='true' className='h-3 w-px' style={{ background: 'var(--fz-rule)' }} />

                        <button
                            type='button'
                            onClick={toggleTheme}
                            className={cn(MICRO, 'pb-1 transition-colors duration-300 hover:opacity-100')}
                            style={{ color: 'var(--fz-faint)' }}
                        >
                            {theme === 'dark' ? 'Light' : 'Dark'}
                        </button>
                    </nav>

                    <button
                        type='button'
                        onClick={() => setMenuOpen(true)}
                        aria-expanded={menuOpen}
                        className={cn(MICRO, 'pointer-events-auto sm:hidden')}
                        style={{ color: 'var(--fz-faint)' }}
                    >
                        Menu
                    </button>
                </div>
            </header>

            <div
                className={cn(
                    'fixed inset-0 z-[80] flex flex-col justify-between px-6 py-5 transition-opacity duration-500 sm:hidden',
                    menuOpen ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
                )}
                style={{
                    background: 'var(--fz-bg)',
                    paddingTop: 'max(1.25rem, env(safe-area-inset-top))'
                }}
                aria-hidden={!menuOpen}
            >
                <div className='flex items-center justify-between'>
                    <span
                        className='font-serif text-[19px] leading-none tracking-[-0.01em]'
                        style={{ color: 'var(--fz-ink)' }}
                    >
                        {siteConfig.name}
                    </span>

                    <button
                        type='button'
                        onClick={() => setMenuOpen(false)}
                        className={MICRO}
                        style={{ color: 'var(--fz-faint)' }}
                        tabIndex={menuOpen ? 0 : -1}
                    >
                        Close
                    </button>
                </div>

                <nav aria-label='Mobile' className='flex flex-col gap-1 pb-4'>
                    {NAV_LINKS.map((link, index) => (
                        <Link
                            key={link.href}
                            href={link.href}
                            tabIndex={menuOpen ? 0 : -1}
                            className='font-serif leading-[1.08] tracking-[-0.02em] transition-all duration-500'
                            style={{
                                fontSize: 'clamp(2.75rem, 15vw, 4.5rem)',
                                color: isActive(link.href) ? 'var(--fz-ink)' : 'var(--fz-sub)',
                                transitionDelay: `${menuOpen ? 90 + index * 70 : 0}ms`,
                                opacity: menuOpen ? 1 : 0,
                                transform: menuOpen ? 'translateY(0)' : 'translateY(14px)',
                                filter: menuOpen ? 'blur(0)' : 'blur(6px)'
                            }}
                        >
                            {link.label}
                        </Link>
                    ))}
                </nav>

                <div className='flex items-center justify-between pb-2'>
                    <span className={MICRO} style={{ color: 'var(--fz-faint)' }}>
                        Est. {studio.founded}
                    </span>

                    <button
                        type='button'
                        onClick={toggleTheme}
                        className={MICRO}
                        style={{ color: 'var(--fz-faint)' }}
                        tabIndex={menuOpen ? 0 : -1}
                    >
                        {theme === 'dark' ? 'Light' : 'Dark'}
                    </button>
                </div>
            </div>
        </>
    )
}
